import type { Article } from '../types/content';

const seo = (slug: string, title: string, metaDescription: string) => ({
  title: `${title} | Insights | Velnoris Executive Network`,
  metaDescription,
  canonicalPath: `/insights/${slug}`,
  noindex: false,
  socialImageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/e832f869-4f48-49a1-821d-0f10eaf14e68.jpg',
});

export const articles: Article[] = [
  {
    slug: 'assessing-leaders-for-scope-they-have-not-held',
    title: 'Assessing Leaders for Scope They Have Not Yet Held',
    standfirst:
      'Most senior appointments ask someone to operate one step beyond their demonstrated scope. The interview process rarely tests for that honestly.',
    category: 'leadership_assessment',
    authorName: 'Search Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-07-14',
    updatedAt: '2026-08-06',
    readingTimeMinutes: 7,
    publishState: 'published',
    heroImageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/e832f869-4f48-49a1-821d-0f10eaf14e68.jpg',
    body: [
      {
        type: 'paragraph',
        text: 'Almost every appointment worth making is a stretch. If the organization could find someone who had already done precisely the job, at precisely the scale, in precisely the context, it would usually promote from within. The external search exists because the requirement has changed.',
      },
      {
        type: 'paragraph',
        text: 'That makes the central assessment question uncomfortable: how do you evidence capability at a scope the candidate has not yet operated at? Interview processes tend to avoid the question by proxy. They look for the largest number on the résumé and assume it transfers.',
      },
      { type: 'heading', text: 'Scale is not the variable that matters most' },
      {
        type: 'paragraph',
        text: 'In our experience the variable that predicts a difficult transition is not the size of the jump in headcount or budget. It is the change in the nature of the decisions. A leader moving from a functional seat to an enterprise one is not doing more of the same work; they are making decisions with materially worse information, against stakeholders who are peers rather than reports.',
      },
      {
        type: 'paragraph',
        text: 'A specification written in terms of scale — revenue, headcount, budget — will screen for the wrong thing. A specification written in terms of decision type screens for something closer to the actual requirement.',
      },
      { type: 'heading', text: 'What to look for instead' },
      {
        type: 'list',
        items: [
          'Decisions the candidate made with incomplete information, and what they did to reduce the uncertainty before committing',
          'Occasions where they were overruled by peers, and how the relationship survived it',
          'Evidence of having changed their own mind on something material, with the reasoning',
          'Where they have built capability they did not personally possess, rather than working around the gap',
          'The specific mechanism by which they learned the last unfamiliar domain they had to lead',
        ],
      },
      { type: 'heading', text: 'Two assessors, one framework' },
      {
        type: 'paragraph',
        text: 'None of this survives an unstructured process. A single assessor working from impression will reliably favour candidates who resemble the last successful appointment. Two assessors working independently against a written framework, calibrating afterwards, produce a materially more defensible view — and one that can be explained to the candidate, which matters as much.',
      },
      {
        type: 'paragraph',
        text: 'That last point is not incidental. A candidate who is not appointed deserves to know what the evidence base was. A process that cannot articulate it was probably not measuring anything.',
      },
    ],
    seo: seo(
      'assessing-leaders-for-scope-they-have-not-held',
      'Assessing Leaders for Scope They Have Not Yet Held',
      'Why scale is the wrong screen for senior appointments, and what to assess instead.',
    ),
  },
  {
    slug: 'what-ai-leadership-specifications-get-wrong',
    title: 'What AI Leadership Specifications Get Wrong',
    standfirst:
      'Organizations are writing research specifications for applied problems, then concluding that the market is empty.',
    category: 'technology_and_ai_leadership',
    authorName: 'Technology Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-06-28',
    updatedAt: '2026-07-19',
    readingTimeMinutes: 6,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'A recurring pattern in briefing conversations: the organization needs someone to industrialize a handful of models into production systems that finance will trust and operations will actually use. The specification it has written asks for a publication record.',
      },
      {
        type: 'paragraph',
        text: 'These are different populations. Conflating them shrinks the addressable market to a few hundred people globally, most of whom do not want the job being described.',
      },
      { type: 'heading', text: 'Separate the three requirements' },
      {
        type: 'list',
        items: [
          'Research leadership: advancing the state of the art. Small population, academic credentials genuinely relevant, rarely what an operating company needs.',
          'Applied engineering leadership: turning models into reliable production systems. Much larger population, assessed on delivered systems and reliability outcomes.',
          'Enterprise adoption leadership: changing how an organization makes decisions. Assessed on organizational change, not on technical depth at all.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Most specifications we are asked to work from describe the third requirement, screen for the first, and are disappointed by candidates from the second.',
      },
      { type: 'heading', text: 'The governance requirement is now explicit' },
      {
        type: 'paragraph',
        text: 'One genuine addition to the requirement over the last two years is model governance: documented evaluation, monitoring for drift, and a defensible position on how automated outputs affect people. Where a system can produce an adverse outcome for an individual, the leadership requirement includes ensuring a human review step exists and is real rather than nominal. That is a governance competency and should be specified as one.',
      },
    ],
    seo: seo(
      'what-ai-leadership-specifications-get-wrong',
      'What AI Leadership Specifications Get Wrong',
      'Three distinct AI leadership requirements that specifications routinely conflate.',
    ),
  },
  {
    slug: 'the-quiet-work-of-confidential-search',
    title: 'The Quiet Work of a Confidential Search',
    standfirst:
      'Confidentiality is an operational discipline with specific costs. It is not a marketing device, and it should never obscure whether a role is real.',
    category: 'executive_hiring',
    authorName: 'Search Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-06-10',
    updatedAt: '2026-06-10',
    readingTimeMinutes: 5,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'A confidential search is one where the client’s identity is withheld from the market, usually because an incumbent remains in seat. It is a legitimate and often necessary instruction. It is also frequently misused.',
      },
      { type: 'heading', text: 'What legitimate confidentiality looks like' },
      {
        type: 'paragraph',
        text: 'The candidate is told plainly that a restriction exists, why it exists, and at what point it will lift. They are told before their profile goes anywhere, and they authorize that step separately. The verification status of the role is disclosed regardless: a confidential mandate is still either a confirmed vacancy or it is not, and the candidate is entitled to know which.',
      },
      { type: 'heading', text: 'What misuse looks like' },
      {
        type: 'list',
        items: [
          'Withholding a client name to disguise the fact that no mandate has been commissioned',
          'Using confidentiality to manufacture scarcity or urgency',
          'Collecting candidate documents against an undisclosed role and circulating them speculatively',
          'Presenting a profile before the candidate has authorized that specific presentation',
        ],
      },
      {
        type: 'paragraph',
        text: 'The last of these is the one that does lasting damage, and it is entirely avoidable. Authorization should be a recorded event tied to a specific mandate, not a checkbox absorbed during registration.',
      },
    ],
    seo: seo(
      'the-quiet-work-of-confidential-search',
      'The Quiet Work of a Confidential Search',
      'How confidential search should work, and the four ways it is commonly misused.',
    ),
  },
  {
    slug: 'succession-that-is-more-than-a-document',
    title: 'Succession That Is More Than a Document',
    standfirst:
      'Most succession plans name people without evidencing readiness, and have no external benchmark at all.',
    category: 'succession_planning',
    authorName: 'Board Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-05-22',
    updatedAt: '2026-07-02',
    readingTimeMinutes: 6,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'Ask a board for its succession plan and you will usually receive a grid. Names sit against seats, colour-coded by readiness. The colours are opinions.',
      },
      { type: 'heading', text: 'Three questions that expose a weak plan' },
      {
        type: 'list',
        items: [
          'What specific evidence supports the readiness rating for each named successor?',
          'Against what external market has that readiness been benchmarked?',
          'What would each successor need to have done, by when, to move a rating?',
        ],
      },
      {
        type: 'paragraph',
        text: 'A plan that cannot answer the first question is a set of preferences. A plan that cannot answer the second has no idea whether its internal candidates are competitive. A plan that cannot answer the third will produce the same grid next year.',
      },
      { type: 'heading', text: 'The external benchmark is the uncomfortable part' },
      {
        type: 'paragraph',
        text: 'Boards resist external benchmarking of internal successors because of what it might reveal, and because of the signalling risk if it becomes known. Both concerns are manageable: the benchmark is anonymized, the external candidates are approached against a described requirement rather than a named vacancy, and the output goes to a defined sponsor group. What is not manageable is discovering the gap during an unplanned departure.',
      },
    ],
    seo: seo(
      'succession-that-is-more-than-a-document',
      'Succession That Is More Than a Document',
      'Three questions that separate a real succession capability from a colour-coded grid.',
    ),
  },
  {
    slug: 'compensation-benchmarks-and-their-limits',
    title: 'Compensation Benchmarks and Their Limits',
    standfirst:
      'Every compensation observation carries a sample size. Most are quoted without one, which is how ranges become folklore.',
    category: 'compensation_trends',
    authorName: 'Research Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-05-04',
    updatedAt: '2026-05-04',
    readingTimeMinutes: 5,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'A range quoted without its sample size, its date, and its definition of scope is not data. It is a rumour with a dollar sign attached.',
      },
      { type: 'heading', text: 'What a usable benchmark states' },
      {
        type: 'list',
        items: [
          'The number of observations behind the range, and how they were gathered',
          'The date range over which they were collected',
          'The definition of scope used to decide that a role is comparable',
          'Whether the figures are base only, or include short-term and long-term incentive',
          'What proportion of the sample is public-company data, which is systematically more visible',
        ],
      },
      {
        type: 'paragraph',
        text: 'On this platform, any compensation figure attached to an opportunity is labelled either client-confirmed or estimated. Estimated figures are research observations, offered so a candidate can decide whether a conversation is worth having. They are never a commitment and never a prediction of an individual outcome.',
      },
    ],
    seo: seo(
      'compensation-benchmarks-and-their-limits',
      'Compensation Benchmarks and Their Limits',
      'What a usable executive compensation benchmark has to state about itself.',
    ),
  },
  {
    slug: 'leadership-in-the-first-hundred-days',
    title: 'Leadership in the First Hundred Days of Sponsor Ownership',
    standfirst:
      'The appointment that works in year three of a hold is often the wrong appointment for month one.',
    category: 'private_equity',
    authorName: 'Private Equity Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-04-18',
    updatedAt: '2026-06-01',
    readingTimeMinutes: 6,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'Sponsor-backed leadership requirements change across the hold period more sharply than in most corporate contexts, and the specification is usually written once.',
      },
      { type: 'heading', text: 'Stage, not sector, is the primary screen' },
      {
        type: 'paragraph',
        text: 'A leader who has separated a carve-out from a parent, stood up its own back office and stabilized reporting has a demonstrably different capability from one who has scaled a stable asset. Both are valuable. They are not substitutes, and sector familiarity does not bridge the gap.',
      },
      { type: 'heading', text: 'Say the incentive structure out loud, early' },
      {
        type: 'paragraph',
        text: 'Processes that avoid the equity conversation until the offer stage waste months. Candidates at this level are comparing an illiquid, structured package against a liquid one and need the shape of it early enough to make a considered decision.',
      },
      { type: 'heading', text: 'Assessment discipline survives compression' },
      {
        type: 'paragraph',
        text: 'Transaction timetables compress the process. They do not remove the requirement for written assessment, structured referencing and a second assessor. Where a search has to run in weeks rather than months, the saving comes from parallelizing research and scheduling, not from lowering the evidence bar.',
      },
    ],
    seo: seo(
      'leadership-in-the-first-hundred-days',
      'Leadership in the First Hundred Days of Sponsor Ownership',
      'Why stage experience outranks sector experience in portfolio leadership appointments.',
    ),
  },
  {
    slug: 'the-board-facing-security-leader',
    title: 'The Board-Facing Security Leader',
    standfirst:
      'Security leadership has become a governance role. Many specifications still describe a technical one.',
    category: 'board_leadership',
    authorName: 'Technology Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-04-02',
    updatedAt: '2026-04-02',
    readingTimeMinutes: 5,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'The chief information security officer now reports on risk to a board committee, holds a documented position on disclosure, and carries personal exposure for the accuracy of what is said. That is a governance role with technical foundations, and it should be specified accordingly.',
      },
      { type: 'heading', text: 'What the board actually needs to hear' },
      {
        type: 'list',
        items: [
          'A quantified view of the exposure the organization is choosing to accept, and why',
          'What would have to be true for a material incident to become a disclosable one',
          'Where the control environment is genuinely weakest, stated before an incident rather than after',
          'What the security function will stop doing in order to do the priorities properly',
        ],
      },
      {
        type: 'paragraph',
        text: 'Assessing for this means testing how a candidate has handled being the bearer of unwelcome quantified news to people who could remove them. It is not testable through a technical interview.',
      },
    ],
    seo: seo(
      'the-board-facing-security-leader',
      'The Board-Facing Security Leader',
      'Why security leadership specifications need to describe a governance role.',
    ),
  },
  {
    slug: 'clinical-and-commercial-leadership',
    title: 'Clinical Credibility and Commercial Capability',
    standfirst:
      'Health-system specifications frequently require both in one person. Deciding which the seat actually needs is the first piece of work.',
    category: 'healthcare_leadership',
    authorName: 'Healthcare Practice Editorial',
    authorTitle: 'Velnoris Executive Network',
    publishedAt: '2026-03-11',
    updatedAt: '2026-05-15',
    readingTimeMinutes: 6,
    publishState: 'published',
    body: [
      {
        type: 'paragraph',
        text: 'A provider organization appointing an operating leader will often specify clinical credibility and commercial capability with equal weight. The population holding both at the required depth is small, and the search stalls.',
      },
      { type: 'heading', text: 'Ask what the credibility is for' },
      {
        type: 'paragraph',
        text: 'Clinical credibility is usually required for one of two reasons: to make clinical governance decisions defensible, or to hold the confidence of the medical staff. The first is a qualification requirement. The second is a relationship requirement, and can sometimes be met structurally — by pairing the seat with a chief medical officer who holds it — rather than by insisting one individual embodies both.',
      },
      {
        type: 'paragraph',
        text: 'Making that decision explicitly, before research begins, is frequently the difference between a search that closes and one that does not.',
      },
      { type: 'heading', text: 'The board is not a single client' },
      {
        type: 'paragraph',
        text: 'Health-system boards commonly include clinicians, community representatives and finance specialists whose criteria differ materially. Calibrating those criteria into a single written specification, and getting it agreed before approach, prevents a shortlist being assessed against three different unstated standards.',
      },
    ],
    seo: seo(
      'clinical-and-commercial-leadership',
      'Clinical Credibility and Commercial Capability',
      'How to decide which credibility a health-system leadership seat genuinely requires.',
    ),
  },
];

export const getArticle = (slug: string): Article | undefined => articles.find((a) => a.slug === slug);
